// UnitComponentsPanel.js
// Full-featured panel for managing physical components attached to an inventory unit.
// Supports add, edit, retire (swap-out with history) and hard delete (mistake correction).
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../scripts/api';

const COMP_TYPES = ['body', 'chassis', 'attachment', 'other'];

const EMPTY_FORM = {
  component_type: 'chassis',
  stock_num: '',
  make_model: '',
  vin: '',
  serial_num: '',
  factory_wo: '',
  factory_prod_so: '',
  po_number: '',
  sort_order: 0,
  is_customer_supplied: false,
  // financials
  net_cost: '',
  surcharge: '',
  tariff: '',
  exchange_rate: '',
  freight_charges: '',
  sell_price: '',
  // dates
  build_date: '',
  arrive_eta: '',
  ready_at_factory: '',
  arrive_sna: '',
  to_customer: '',
  status_update: '',
  // status
  location: '',
  status_note: '',
  notes: '',
};

function fmtCurrency(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(v) {
  if (!v) return null;
  return v.slice(0, 10); // YYYY-MM-DD
}

// ── Field row helpers ──────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder }) {
  return (
    <input
      className="input"
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: '100%' }}
    />
  );
}

// ── Section header ─────────────────────────────────────────────────────────

function Section({ title }) {
  return (
    <div style={{
      gridColumn: '1 / -1',
      fontWeight: 700,
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      opacity: 0.5,
      borderBottom: '1px solid #eee',
      paddingBottom: 4,
      marginTop: 8,
    }}>
      {title}
    </div>
  );
}

// ── Component form (add or edit) ───────────────────────────────────────────

function ComponentForm({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '10px 16px',
        marginBottom: 16,
      }}>
        <Section title="Identity" />

        <Field label="Type">
          <select className="input" value={form.component_type} onChange={(e) => set('component_type')(e.target.value)} style={{ width: '100%' }}>
            {COMP_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </Field>

        <Field label="Stock #">
          <Input value={form.stock_num} onChange={set('stock_num')} placeholder="e.g. U9000B" />
        </Field>

        <Field label="Make / Model">
          <Input value={form.make_model} onChange={set('make_model')} placeholder="e.g. Wittke Starlight" />
        </Field>

        <Field label="VIN">
          <Input value={form.vin} onChange={set('vin')} placeholder="VIN" />
        </Field>

        <Field label="Serial #">
          <Input value={form.serial_num} onChange={set('serial_num')} placeholder="Serial number" />
        </Field>

        <Field label="Factory WO">
          <Input value={form.factory_wo} onChange={set('factory_wo')} placeholder="Factory work order" />
        </Field>

        <Field label="Factory Prod SO">
          <Input value={form.factory_prod_so} onChange={set('factory_prod_so')} placeholder="Production SO" />
        </Field>

        <Field label="PO Number">
          <Input value={form.po_number} onChange={set('po_number')} placeholder="Purchase order #" />
        </Field>

        <Field label="Customer Supplied?">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <input
              type="checkbox"
              checked={!!form.is_customer_supplied}
              onChange={(e) => set('is_customer_supplied')(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>Customer-supplied (no SNA cost)</span>
          </label>
        </Field>

        <Section title="Financials" />

        <Field label="Net Cost">
          <Input type="number" value={form.net_cost} onChange={set('net_cost')} placeholder="0.00" />
        </Field>

        <Field label="Surcharge">
          <Input type="number" value={form.surcharge} onChange={set('surcharge')} placeholder="0.00" />
        </Field>

        <Field label="Tariff">
          <Input type="number" value={form.tariff} onChange={set('tariff')} placeholder="0.00" />
        </Field>

        <Field label="Exchange Rate">
          <Input type="number" value={form.exchange_rate} onChange={set('exchange_rate')} placeholder="1.0000" />
        </Field>

        <Field label="Freight">
          <Input type="number" value={form.freight_charges} onChange={set('freight_charges')} placeholder="0.00" />
        </Field>

        <Field label="Sell Price">
          <Input type="number" value={form.sell_price} onChange={set('sell_price')} placeholder="0.00" />
        </Field>

        <Section title="Dates" />

        <Field label="Build Date">
          <Input type="date" value={form.build_date} onChange={set('build_date')} />
        </Field>

        <Field label="Arrive ETA">
          <Input type="date" value={form.arrive_eta} onChange={set('arrive_eta')} />
        </Field>

        <Field label="Ready at Factory">
          <Input type="date" value={form.ready_at_factory} onChange={set('ready_at_factory')} />
        </Field>

        <Field label="Arrive SNA">
          <Input type="date" value={form.arrive_sna} onChange={set('arrive_sna')} />
        </Field>

        <Field label="To Customer">
          <Input type="date" value={form.to_customer} onChange={set('to_customer')} />
        </Field>

        <Field label="Status Update">
          <Input type="date" value={form.status_update} onChange={set('status_update')} />
        </Field>

        <Section title="Status & Notes" />

        <Field label="Location">
          <Input value={form.location} onChange={set('location')} placeholder="Current location" />
        </Field>

        <Field label="Status Note">
          <Input value={form.status_note} onChange={set('status_note')} placeholder="Status details" />
        </Field>

        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Notes">
            <textarea
              className="input"
              rows={2}
              value={form.notes ?? ''}
              onChange={(e) => set('notes')(e.target.value)}
              placeholder="Internal notes"
              style={{ width: '100%', resize: 'vertical' }}
            />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save Component'}
        </button>
        <button className="btn-outline" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Retire modal ───────────────────────────────────────────────────────────

function RetireModal({ comp, onConfirm, onCancel, busy }) {
  const [note, setNote] = useState('');
  return (
    <div style={{ padding: 12, border: '1px solid #f0a', borderRadius: 8, background: '#fff8fb', marginTop: 8 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>
        Retire {comp.component_type} — {comp.make_model || comp.stock_num || `#${comp.id}`}?
      </div>
      <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>
        The component will be marked retired and kept in history. Use this when swapping out a chassis or replacing a body.
        To correct a mistake, use Delete instead.
      </div>
      <textarea
        className="input"
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Reason for retirement (e.g. chassis replaced, sold separately)"
        style={{ width: '100%', marginBottom: 8, resize: 'vertical' }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" style={{ background: '#c00', borderColor: '#c00' }} onClick={() => onConfirm(note)} disabled={busy}>
          {busy ? 'Retiring…' : 'Confirm Retire'}
        </button>
        <button className="btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}

// ── Single component row (collapsed summary + expand) ─────────────────────

function ComponentRow({ comp, onEdit, onRetire, onRestore, onDelete, busy, retired }) {
  const [expanded, setExpanded] = useState(false);

  const hasFinancials = comp.net_cost || comp.surcharge || comp.tariff || comp.freight_charges || comp.sell_price;
  const hasDates = comp.build_date || comp.arrive_eta || comp.ready_at_factory || comp.arrive_sna || comp.to_customer;

  return (
    <div style={{
      border: `1px solid ${retired ? '#ddd' : '#e0e8f0'}`,
      borderRadius: 8,
      marginBottom: 8,
      opacity: retired ? 0.65 : 1,
      background: retired ? '#fafafa' : '#fff',
    }}>
      {/* Summary row */}
      <div
        style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 12px', cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 700 }}>{comp.make_model || '(no model)'}</span>
          {comp.is_customer_supplied && (
            <span style={{ marginLeft: 8, fontSize: 11, background: '#e8f4ff', color: '#0066cc', padding: '1px 6px', borderRadius: 10 }}>
              Cust. Supplied
            </span>
          )}
          {retired && (
            <span style={{ marginLeft: 8, fontSize: 11, background: '#fee', color: '#900', padding: '1px 6px', borderRadius: 10 }}>
              Retired
            </span>
          )}
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {comp.stock_num && <span>Stock: {comp.stock_num}</span>}
            {comp.vin && <span>VIN: {comp.vin}</span>}
            {comp.po_number && <span>PO: {comp.po_number}</span>}
            {comp.net_cost && <span>Cost: {fmtCurrency(comp.net_cost)}</span>}
            {comp.location && <span>📍 {comp.location}</span>}
          </div>
          {comp.retired_note && (
            <div style={{ fontSize: 11, color: '#900', marginTop: 2 }}>Retired: {comp.retired_note}</div>
          )}
        </div>
        <div style={{ fontSize: 12, opacity: 0.4 }}>{expanded ? '▲' : '▼'}</div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: '0 12px 12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '6px 16px', marginBottom: 10, fontSize: 13 }}>
            {comp.serial_num && <div><span style={{ opacity: 0.6 }}>Serial:</span> {comp.serial_num}</div>}
            {comp.factory_wo && <div><span style={{ opacity: 0.6 }}>Factory WO:</span> {comp.factory_wo}</div>}
            {comp.factory_prod_so && <div><span style={{ opacity: 0.6 }}>Prod SO:</span> {comp.factory_prod_so}</div>}

            {hasFinancials && <>
              {comp.net_cost && <div><span style={{ opacity: 0.6 }}>Net Cost:</span> {fmtCurrency(comp.net_cost)}</div>}
              {comp.surcharge && <div><span style={{ opacity: 0.6 }}>Surcharge:</span> {fmtCurrency(comp.surcharge)}</div>}
              {comp.tariff && <div><span style={{ opacity: 0.6 }}>Tariff:</span> {fmtCurrency(comp.tariff)}</div>}
              {comp.exchange_rate && <div><span style={{ opacity: 0.6 }}>Exch. Rate:</span> {comp.exchange_rate}</div>}
              {comp.freight_charges && <div><span style={{ opacity: 0.6 }}>Freight:</span> {fmtCurrency(comp.freight_charges)}</div>}
              {comp.sell_price && <div><span style={{ opacity: 0.6 }}>Sell:</span> {fmtCurrency(comp.sell_price)}</div>}
            </>}

            {hasDates && <>
              {comp.build_date && <div><span style={{ opacity: 0.6 }}>Build Date:</span> {fmtDate(comp.build_date)}</div>}
              {comp.arrive_eta && <div><span style={{ opacity: 0.6 }}>Arrive ETA:</span> {fmtDate(comp.arrive_eta)}</div>}
              {comp.ready_at_factory && <div><span style={{ opacity: 0.6 }}>Ready at Factory:</span> {fmtDate(comp.ready_at_factory)}</div>}
              {comp.arrive_sna && <div><span style={{ opacity: 0.6 }}>Arrive SNA:</span> {fmtDate(comp.arrive_sna)}</div>}
              {comp.to_customer && <div><span style={{ opacity: 0.6 }}>To Customer:</span> {fmtDate(comp.to_customer)}</div>}
              {comp.status_update && <div><span style={{ opacity: 0.6 }}>Status Update:</span> {fmtDate(comp.status_update)}</div>}
            </>}

            {comp.status_note && <div style={{ gridColumn: '1 / -1' }}><span style={{ opacity: 0.6 }}>Status:</span> {comp.status_note}</div>}
            {comp.notes && <div style={{ gridColumn: '1 / -1' }}><span style={{ opacity: 0.6 }}>Notes:</span> {comp.notes}</div>}
            {comp.retired_at && <div style={{ gridColumn: '1 / -1', color: '#900', fontSize: 12 }}>Retired {fmtDate(comp.retired_at)}{comp.retired_note ? ` — ${comp.retired_note}` : ''}</div>}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {!retired && (
              <button className="btn-outline" style={{ fontSize: 12 }} onClick={() => onEdit(comp)} disabled={busy}>Edit</button>
            )}
            {retired && (
              <button className="btn-outline" style={{ fontSize: 12, color: '#060' }} onClick={() => onRestore(comp.id)} disabled={busy}>Restore</button>
            )}
            <details style={{ position: 'relative', display: 'inline-block' }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, opacity: 0.45, listStyle: 'none', padding: '3px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
                ···
              </summary>
              <div style={{ position: 'absolute', right: 0, top: '100%', background: '#fff', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: 160, padding: 4 }}>
                {!retired && (
                  <button
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#c60' }}
                    onClick={() => { document.activeElement.blur(); onRetire(comp); }}
                    disabled={busy}
                  >
                    Retire / Swap Out…
                  </button>
                )}
                <button
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#c00' }}
                  onClick={() => { document.activeElement.blur(); onDelete(comp.id); }}
                  disabled={busy}
                >
                  Delete (mistake only)
                </button>
              </div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────

export default function UnitComponentsPanel({ unitId }) {
  const [components, setComponents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // mode: null | 'add' | { editing: comp } | { retiring: comp }
  const [mode, setMode] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/unit-components/${unitId}`);
      setComponents(data.components || []);
    } catch (e) {
      console.error('Load components failed', e);
    }
  }, [unitId]);

  useEffect(() => { reload(); }, [reload]);

  const handleAdd = async (form) => {
    setBusy(true); setMsg('');
    try {
      await api.post(`/unit-components/${unitId}`, form);
      await reload();
      setMode(null);
      setMsg('Component added.');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to add component');
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async (form) => {
    setBusy(true); setMsg('');
    try {
      await api.patch(`/unit-components/component/${mode.editing.id}`, form);
      await reload();
      setMode(null);
      setMsg('Component updated.');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to update component');
    } finally {
      setBusy(false);
    }
  };

  const handleRetire = async (note) => {
    setBusy(true); setMsg('');
    try {
      await api.post(`/unit-components/component/${mode.retiring.id}/retire`, { retired_note: note });
      await reload();
      setMode(null);
      setMsg('Component retired.');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to retire component');
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (compId) => {
    setBusy(true); setMsg('');
    try {
      await api.post(`/unit-components/component/${compId}/restore`);
      await reload();
      setMsg('Component restored.');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to restore component');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (compId) => {
    if (!window.confirm('Permanently delete this component? Use Retire instead if you are swapping it out.')) return;
    setBusy(true); setMsg('');
    try {
      await api.delete(`/unit-components/component/${compId}`);
      setComponents((prev) => prev.filter((c) => c.id !== compId));
      setMsg('Component deleted.');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const active = components.filter((c) => !c.retired_at);
  const retired = components.filter((c) => c.retired_at);

  return (
    <div className="card" style={{ minWidth: 0, marginTop: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>
          Components
          {active.length > 0 && (
            <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 13, opacity: 0.55 }}>
              {active.length} active{retired.length > 0 ? `, ${retired.length} retired` : ''}
            </span>
          )}
        </div>
        {mode === null && (
          <button className="btn-outline" type="button" onClick={() => setMode('add')}>
            + Add Component
          </button>
        )}
      </div>

      {msg && <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      {/* Add form */}
      {mode === 'add' && (
        <div style={{ padding: 12, border: '1px solid #c8dff0', borderRadius: 8, background: '#f8fbff', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>New Component</div>
          <ComponentForm
            initial={EMPTY_FORM}
            onSave={handleAdd}
            onCancel={() => setMode(null)}
            busy={busy}
          />
        </div>
      )}

      {/* Edit form */}
      {mode?.editing && (
        <div style={{ padding: 12, border: '1px solid #c8dff0', borderRadius: 8, background: '#f8fbff', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>
            Edit — {mode.editing.component_type} {mode.editing.make_model || mode.editing.stock_num || `#${mode.editing.id}`}
          </div>
          <ComponentForm
            initial={mode.editing}
            onSave={handleEdit}
            onCancel={() => setMode(null)}
            busy={busy}
          />
        </div>
      )}

      {/* Active components by type */}
      {active.length === 0 && mode === null && (
        <div className="muted" style={{ fontSize: 13 }}>
          No active components. Add a chassis, body, or attachment to track its identity, financials, and lifecycle separately from the unit.
        </div>
      )}

      {COMP_TYPES.map((type) => {
        const group = active.filter((c) => c.component_type === type);
        if (!group.length) return null;
        return (
          <div key={type} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, marginBottom: 6, letterSpacing: 0.4 }}>
              {type}
            </div>
            {group.map((c) => (
              <div key={c.id}>
                <ComponentRow
                  comp={c}
                  retired={false}
                  busy={busy}
                  onEdit={(comp) => setMode({ editing: comp })}
                  onRetire={(comp) => setMode({ retiring: comp })}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                />
                {mode?.retiring?.id === c.id && (
                  <RetireModal
                    comp={c}
                    onConfirm={handleRetire}
                    onCancel={() => setMode(null)}
                    busy={busy}
                  />
                )}
              </div>
            ))}
          </div>
        );
      })}

      {/* Retired components (collapsed section) */}
      {retired.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, opacity: 0.55, userSelect: 'none', marginBottom: 8 }}>
            {retired.length} retired component{retired.length !== 1 ? 's' : ''} (history)
          </summary>
          {retired.map((c) => (
            <ComponentRow
              key={c.id}
              comp={c}
              retired={true}
              busy={busy}
              onEdit={() => {}}
              onRetire={() => {}}
              onRestore={handleRestore}
              onDelete={handleDelete}
            />
          ))}
        </details>
      )}
    </div>
  );
}
